import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import SpecialOfferForm from './SpecialOfferForm';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function AdminPanel() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [businessCustomers, setBusinessCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // State for editing offers from admin panel
  const [editingOffer, setEditingOffer] = useState(null);
  const [editingPark, setEditingPark] = useState(null);
  const [loadingOffer, setLoadingOffer] = useState(false);
  
  // Business customer list management
  const [expandedCustomers, setExpandedCustomers] = useState(new Set());
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('businessCustomerSort') || 'subscription'; // Default: shortest subscription first
  });
  const [sortDirection, setSortDirection] = useState(() => {
    return localStorage.getItem('businessCustomerSortDirection') || 'asc'; // asc = shortest first, desc = longest first
  });
  
  // User Management sort state
  const [userSortBy, setUserSortBy] = useState(() => {
    return localStorage.getItem('userManagementSort') || 'date'; // Default: date joined (newest first)
  });
  const [userSortDirection, setUserSortDirection] = useState(() => {
    return localStorage.getItem('userManagementSortDirection') || 'desc'; // desc = newest first
  });
  
  // DelightOverlay toggle state (master account only)
  const [delightOverlayEnabled, setDelightOverlayEnabled] = useState(() => {
    return localStorage.getItem('delightOverlayEnabled') === 'true';
  });
  
  // Handle DelightOverlay toggle
  const handleDelightOverlayToggle = () => {
    const newState = !delightOverlayEnabled;
    setDelightOverlayEnabled(newState);
    localStorage.setItem('delightOverlayEnabled', newState.toString());
    toast.success(`DelightOverlay ${newState ? 'enabled' : 'disabled'} - Reload page to apply`);
  };
  
  // Fetch users
  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  
  // Load users on mount
  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'business') {
      fetchBusinessCustomers();
    }
  }, [activeTab]);
  
  // Fetch business customers from API
  const fetchBusinessCustomers = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/business-customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinessCustomers(response.data.customers);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load business customers');
      console.error('Error fetching business customers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate shortest subscription period for a customer
  const getShortestSubscription = (customer) => {
    if (!customer.subscriptions || customer.subscriptions.length === 0) {
      return { days: 0, isExpired: true };
    }
    const shortest = customer.subscriptions.reduce((min, sub) => {
      return sub.days_remaining < min ? sub.days_remaining : min;
    }, Infinity);
    return { days: shortest, isExpired: shortest <= 0 };
  };

  // Format days remaining as readable text
  const formatDaysRemaining = (days) => {
    if (days <= 0) return 'Expired';
    if (days < 30) return `${days} days`;
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;
    if (remainingDays === 0) return `${months} ${months === 1 ? 'month' : 'months'}`;
    return `${months}m ${remainingDays}d`;
  };

  // Sort business customers
  const getSortedCustomers = () => {
    const sorted = [...businessCustomers].sort((a, b) => {
      switch (sortBy) {
        case 'alphabetical':
          return a.business_name.localeCompare(b.business_name);
        
        case 'creation':
          // Assuming customers have created_at or similar field
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return dateB - dateA; // Newest first
        
        case 'subscription':
        default:
          const shortestA = getShortestSubscription(a).days;
          const shortestB = getShortestSubscription(b).days;
          return sortDirection === 'asc' ? shortestA - shortestB : shortestB - shortestA;
      }
    });
    return sorted;
  };

  // Handle sort change
  const handleSortChange = (newSortBy) => {
    if (newSortBy === sortBy && newSortBy === 'subscription') {
      // Toggle direction for subscription sort
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
      localStorage.setItem('businessCustomerSortDirection', newDirection);
    } else {
      setSortBy(newSortBy);
      localStorage.setItem('businessCustomerSort', newSortBy);
      if (newSortBy === 'subscription') {
        setSortDirection('asc'); // Reset to shortest first
        localStorage.setItem('businessCustomerSortDirection', 'asc');
      }
    }
  };

  // Toggle customer expansion
  const toggleCustomerExpansion = (customerId) => {
    const newExpanded = new Set(expandedCustomers);
    if (newExpanded.has(customerId)) {
      newExpanded.delete(customerId);
    } else {
      newExpanded.add(customerId);
    }
    setExpandedCustomers(newExpanded);
  };

  // Sort users
  const getSortedUsers = () => {
    const sorted = [...users].sort((a, b) => {
      switch (userSortBy) {
        case 'username':
          const usernameCompare = a.username.localeCompare(b.username);
          return userSortDirection === 'asc' ? usernameCompare : -usernameCompare;
        
        case 'email':
          const emailCompare = a.email.localeCompare(b.email);
          return userSortDirection === 'asc' ? emailCompare : -emailCompare;
        
        case 'date':
        default:
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return userSortDirection === 'desc' ? dateB - dateA : dateA - dateB; // desc = newest first
      }
    });
    return sorted;
  };

  // Handle user sort change
  const handleUserSortChange = (newSortBy) => {
    if (newSortBy === userSortBy) {
      // Toggle direction
      const newDirection = userSortDirection === 'asc' ? 'desc' : 'asc';
      setUserSortDirection(newDirection);
      localStorage.setItem('userManagementSortDirection', newDirection);
    } else {
      setUserSortBy(newSortBy);
      localStorage.setItem('userManagementSort', newSortBy);
      // Set default direction based on sort type
      const defaultDirection = newSortBy === 'date' ? 'desc' : 'asc';
      setUserSortDirection(defaultDirection);
      localStorage.setItem('userManagementSortDirection', defaultDirection);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Handle editing offer from admin panel
  const handleEditOffer = async (customer, subscription) => {
    setLoadingOffer(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Fetch park data
      const parkResponse = await axios.get(
        `${API_URL}/api/parks/${subscription.park_id}`,
        config
      );
      
      // Fetch offer data for this park
      const offersResponse = await axios.get(
        `${API_URL}/api/special-offers?park_id=${subscription.park_id}`,
        config
      );
      
      const offers = offersResponse.data.offers || [];
      const offerForPark = offers.find(o => o.park_id === subscription.park_id);
      
      if (!offerForPark) {
        toast.error('No offer found for this business pin');
        return;
      }
      
      // Set state to open modal
      setEditingPark(parkResponse.data);
      setEditingOffer(offerForPark);
      
    } catch (error) {
      console.error('Error loading offer for editing:', error);
      toast.error('Failed to load offer data');
    } finally {
      setLoadingOffer(false);
    }
  };

  
  // Update subscription duration
  const handleSubscriptionUpdate = async (subscriptionId, newMonths, businessEmail) => {
    // Confirmation dialog if shortening subscription
    const confirmed = window.confirm(
      `Update subscription to ${newMonths} months?\n\n` +
      `This will recalculate the end date from the original start date.`
    );
    
    if (!confirmed) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/admin/business-subscriptions/${subscriptionId}`,
        { months_purchased: newMonths },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Subscription updated to ${newMonths} months`);
      fetchBusinessCustomers(); // Refresh
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to update subscription';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };
  
  // Gift subscription time
  const handleGiftTime = async (subscriptionId, businessEmail, parkId) => {
    const months = window.prompt('How many months to add? (1-12)');
    if (!months || parseInt(months) < 1 || parseInt(months) > 12) {
      return;
    }
    
    const reason = window.prompt('Reason for gifting time (optional):') || '';
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/business-subscriptions/${subscriptionId}/gift-time`,
        { months_to_add: parseInt(months), reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`✅ Added ${months} months to subscription for ${businessEmail}`);
      fetchBusinessCustomers();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to gift time';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };
  
  // Check admin access - MOVED AFTER ALL HOOKS
  if (!user || (user.role !== 'admin' && user.role !== 'master')) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-2xl font-bold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600">You do not have permission to access the admin panel.</p>
        </div>
      </div>
    );
  }
  
  // Promote/demote user role
  const handleRoleChange = async (userId, newRole, username) => {
    if (!window.confirm(`Change ${username}'s role to ${newRole}?`)) {
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/admin/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`${username} role updated to ${newRole}`);
      fetchUsers(); // Refresh list
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };
  
  // Delete user
  const handleDeleteUser = async (userId, username) => {
    const confirmText = `DELETE ${username}`;
    const userInput = window.prompt(
      `⚠️ WARNING: This will delete the user account and transfer all their pins to HappyPaw.\n\n` +
      `Type "${confirmText}" to confirm deletion:`
    );
    
    if (userInput !== confirmText) {
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_URL}/api/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`User deleted. ${response.data.pins_transferred} pins transferred to HappyPaw.`);
      fetchUsers(); // Refresh list
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header with Return Button */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Admin Panel</h1>
          <p className="text-gray-600 mt-2">Manage users, pins, and content moderation</p>
        </div>
        <a
          href="/dashboard"
          className="px-4 py-2 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
        >
          ← Return to Dashboard
        </a>
      </div>
      
      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <p className="text-green-800">{success}</p>
        </div>
      )}
      
      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('users')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'users'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              User Management
            </button>
            <button
              onClick={() => setActiveTab('business')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'business'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Business Pins
            </button>
            <button
              onClick={() => setActiveTab('info')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'info'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Admin Info
            </button>
          </nav>
        </div>
        
        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'users' && (
            <div>
              {/* Header with Sort Controls */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">User Management</h2>
                
                {/* Sort Buttons */}
                {users.length > 0 && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleUserSortChange('username')}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        userSortBy === 'username'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      👤 Username {userSortBy === 'username' && (userSortDirection === 'asc' ? '↑' : '↓')}
                    </button>
                    <button
                      onClick={() => handleUserSortChange('email')}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        userSortBy === 'email'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      📧 Email {userSortBy === 'email' && (userSortDirection === 'asc' ? '↑' : '↓')}
                    </button>
                    <button
                      onClick={() => handleUserSortChange('date')}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        userSortBy === 'date'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      📅 Date {userSortBy === 'date' && (userSortDirection === 'asc' ? '↑' : '↓')}
                    </button>
                  </div>
                )}
              </div>
              
              {loading && <p className="text-gray-600">Loading users...</p>}
              
              {!loading && users.length === 0 && (
                <p className="text-gray-600">No users found</p>
              )}
              
              {!loading && users.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Joined</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getSortedUsers().map((u) => (
                        <tr key={u.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {u.username}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {u.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              u.role === 'master' ? 'bg-purple-100 text-purple-800' :
                              u.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {u.is_verified ? (
                              <span className="text-green-600">✓ Verified</span>
                            ) : (
                              <span className="text-gray-400">Unverified</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(u.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                            {u.role !== 'master' && (
                              <>
                                {u.role === 'user' && (
                                  <button
                                    onClick={() => handleRoleChange(u.id, 'admin', u.username)}
                                    className="text-blue-600 hover:text-blue-800"
                                    disabled={loading}
                                  >
                                    Promote to Admin
                                  </button>
                                )}
                                {u.role === 'admin' && (
                                  <button
                                    onClick={() => handleRoleChange(u.id, 'user', u.username)}
                                    className="text-orange-600 hover:text-orange-800"
                                    disabled={loading}
                                  >
                                    Demote to User
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteUser(u.id, u.username)}
                                  className="text-red-600 hover:text-red-800 ml-3"
                                  disabled={loading || u.id === user.id}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                            {u.role === 'master' && (
                              <span className="text-gray-400">Protected</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'business' && (
            <div>
              {/* Header with Sort Controls */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Business Pin Management</h2>
                
                {/* Sort Buttons */}
                {businessCustomers.length > 0 && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSortChange('creation')}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        sortBy === 'creation'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      📅 Creation Date
                    </button>
                    <button
                      onClick={() => handleSortChange('alphabetical')}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        sortBy === 'alphabetical'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      🔤 Alphabetical
                    </button>
                    <button
                      onClick={() => handleSortChange('subscription')}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        sortBy === 'subscription'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      ⏱️ Subscription {sortBy === 'subscription' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </button>
                  </div>
                )}
              </div>
              
              {loading && <p className="text-gray-600">Loading business customers...</p>}
              
              {!loading && businessCustomers.length === 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                  <p className="text-gray-600 mb-2">No business customers yet</p>
                  <p className="text-sm text-gray-500">
                    Business customers will appear here once Phase 2 (Authentication) is complete
                  </p>
                </div>
              )}
              
              {!loading && businessCustomers.length > 0 && (
                <div className="space-y-2">
                  {getSortedCustomers().map((customer) => {
                    const shortestSub = getShortestSubscription(customer);
                    const isExpanded = expandedCustomers.has(customer.id);
                    const showRedFlag = shortestSub.days < 90 && shortestSub.days > 0;
                    
                    return (
                    <div key={customer.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Compact View (Always Visible) */}
                      <div
                        onClick={() => toggleCustomerExpansion(customer.id)}
                        className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <span className="text-lg">
                            {isExpanded ? '▼' : '▶'}
                          </span>
                          <div>
                            <h3 className="text-base font-semibold text-gray-900">
                              {customer.business_name}
                            </h3>
                            <p className="text-xs text-gray-500">
                              {customer.pins_count} {customer.pins_count === 1 ? 'Pin' : 'Pins'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {showRedFlag && <span className="text-red-600 text-lg" title="Subscription < 3 months">🚩</span>}
                          <span className={`text-sm font-medium ${
                            shortestSub.isExpired ? 'text-red-600' :
                            shortestSub.days < 90 ? 'text-red-600' :
                            shortestSub.days < 180 ? 'text-yellow-600' :
                            'text-green-600'
                          }`}>
                            {formatDaysRemaining(shortestSub.days)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Expanded View (Shows when clicked) */}
                      {isExpanded && (
                        <div className="border-t border-gray-200 bg-white p-6">
                          {/* Customer Details */}
                          <div className="mb-4">
                            <p className="text-sm text-gray-500">{customer.email}</p>
                            {customer.contact_name && (
                              <p className="text-sm text-gray-500">Contact: {customer.contact_name}</p>
                            )}
                          </div>
                      
                      {/* Subscriptions */}
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Active Subscriptions</h4>
                        <div className="space-y-2">
                          {customer.subscriptions && customer.subscriptions.length > 0 ? (
                            customer.subscriptions.map((sub, idx) => (
                              <div key={idx} className="bg-gray-50 rounded-lg p-4 flex justify-between items-center">
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">
                                    {sub.park_name || sub.park_id}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Park ID: {sub.park_id}
                                  </p>
                                  <p className="text-sm text-gray-600 mt-1">
                                    Expires: {new Date(sub.end_date).toLocaleDateString()}
                                  </p>
                                  
                                  {/* Inline Subscription Duration Controls */}
                                  <div className="flex items-center gap-2 mt-2">
                                    <label className="text-xs text-gray-600">Duration:</label>
                                    <select
                                      value={sub.months_purchased || 3}
                                      onChange={(e) => handleSubscriptionUpdate(sub.id, parseInt(e.target.value), customer.email)}
                                      className="px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                      disabled={loading}
                                    >
                                      <option value="3">3 Months</option>
                                      <option value="6">6 Months</option>
                                      <option value="9">9 Months</option>
                                      <option value="12">12 Months</option>
                                    </select>
                                    <span className="text-xs text-gray-500">
                                      {sub.days_remaining > 30 ? (
                                        <span className="text-green-600">
                                          ⏱️ {sub.days_remaining} days left
                                        </span>
                                      ) : sub.days_remaining > 7 ? (
                                        <span className="text-yellow-600">
                                          ⚠️ {sub.days_remaining} days left
                                        </span>
                                      ) : (
                                        <span className="text-red-600">
                                          🔴 {sub.days_remaining} days left
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleEditOffer(customer, sub)}
                                    disabled={loadingOffer}
                                    className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded hover:bg-emerald-700 transition-colors disabled:opacity-50"
                                  >
                                    ✏️ Edit Offer
                                  </button>
                                  <button
                                    onClick={() => handleGiftTime(sub.id, customer.email, sub.park_id)}
                                    className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded hover:bg-purple-700 transition-colors"
                                  >
                                    🎁 Gift Time
                                  </button>
                                  <button
                                    className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded hover:bg-gray-700 transition-colors"
                                    title="View edit logs (Phase 5)"
                                  >
                                    📝 View Logs
                                  </button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-sm text-gray-500">No active subscriptions</p>
                          )}
                        </div>
                      </div>
                          
                          {/* Quick Actions */}
                          <div className="mt-4 pt-4 border-t border-gray-200 flex gap-2">
                            <button
                              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors"
                              title="Send to business portal (Phase 4)"
                            >
                              📧 Send Portal Link
                            </button>
                            <button
                              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition-colors"
                              title="View analytics (Phase 5)"
                            >
                              📊 View Analytics
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                  })}
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'info' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Admin Features</h2>
              
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-2">✅ Content Moderation</h3>
                  <p className="text-sm text-blue-800">
                    Delete any pin directly from the map (includes cascade deletion of comments and ratings).
                    Look for the red "Delete Pin (Admin)" button when viewing pins.
                  </p>
                </div>
                
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-purple-900 mb-2">⭐ Featured Pins</h3>
                  <p className="text-sm text-purple-800">
                    Create special featured pins for business customers when adding a new location.
                    Check the "Featured Business Pin" option (admin only).
                  </p>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-green-900 mb-2">👥 User Management</h3>
                  <p className="text-sm text-green-800">
                    Promote users to admin, demote admins, or delete user accounts.
                    User pins transfer to HappyPaw when accounts are deleted.
                  </p>
                </div>
                
                <div className="bg-amber-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-amber-900 mb-2">🔮 Future Features</h3>
                  <p className="text-sm text-amber-800">
                    Featured pin interactions and special offers system coming soon!
                    This will enable business customers to offer downloadable coupons and promotions.
                  </p>
                </div>
                
                {/* DelightOverlay Toggle - Master Account Only */}
                {user.role === 'master' && (
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border-2 border-purple-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-purple-900 mb-1">✨ DelightOverlay Animation</h3>
                        <p className="text-sm text-purple-800">
                          Enable or disable the click-counting animation overlay. Toggle takes effect after page reload.
                        </p>
                      </div>
                      <button
                        onClick={handleDelightOverlayToggle}
                        className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                          delightOverlayEnabled
                            ? 'bg-purple-600 text-white hover:bg-purple-700 shadow-lg'
                            : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                        }`}
                      >
                        {delightOverlayEnabled ? '✓ Enabled' : '✗ Disabled'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Special Offer Form Modal */}
      {editingOffer && editingPark && (
        <SpecialOfferForm
          park={editingPark}
          existingOffer={editingOffer}
          onClose={() => {
            setEditingOffer(null);
            setEditingPark(null);
          }}
          onSuccess={() => {
            setEditingOffer(null);
            setEditingPark(null);
            fetchBusinessCustomers(); // Refresh the business customers list
          }}
        />
      )}
    </div>
  );
}

export default AdminPanel;
